import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';

import { Spinner } from './Spinner.js';
import { cx } from './internal/cx.js';

export type TableScrollAreaProps = ComponentPropsWithoutRef<'div'> & {
  children: ReactNode;
  /** When true, replaces children with a centered Spinner. */
  loading?: boolean;
  /** Accessible label for the loading Spinner. */
  loadingLabel?: string;
};

function TableScrollArea({
  children,
  className,
  loading = false,
  loadingLabel = 'Loading',
  ...props
}: TableScrollAreaProps): JSX.Element {
  if (loading) {
    return (
      <div className={cx('nrx-table-scroll', 'nrx-table-loading', className)} {...props}>
        <Spinner label={loadingLabel} />
      </div>
    );
  }

  return (
    <div className={cx('nrx-table-scroll', className)} {...props}>
      {children}
    </div>
  );
}

function TableRoot({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'table'> & { children: ReactNode }): JSX.Element {
  return (
    <table className={cx('nrx-table', className)} {...props}>
      {children}
    </table>
  );
}

function TableHeader({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'thead'> & { children: ReactNode }): JSX.Element {
  return (
    <thead className={cx('nrx-table__head', className)} {...props}>
      {children}
    </thead>
  );
}

function TableBody({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'tbody'> & { children: ReactNode }): JSX.Element {
  return (
    <tbody className={cx('nrx-table__body', className)} {...props}>
      {children}
    </tbody>
  );
}

function TableRow({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'tr'> & { children: ReactNode }): JSX.Element {
  return (
    <tr className={cx('nrx-table__row', className)} {...props}>
      {children}
    </tr>
  );
}

function TableColumnHeaderCell({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'th'> & { children: ReactNode }): JSX.Element {
  return (
    <th scope="col" className={cx('nrx-table__head-cell', className)} {...props}>
      {children}
    </th>
  );
}

function TableCell({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'td'> & { children: ReactNode }): JSX.Element {
  return (
    <td className={cx('nrx-table__cell', className)} {...props}>
      {children}
    </td>
  );
}

/** Compound table primitive for data-heavy admin screens. Requires `@import '@nrx/ui/theme.css'`. */
export const Table = {
  ScrollArea: TableScrollArea,
  Root: TableRoot,
  Header: TableHeader,
  Body: TableBody,
  Row: TableRow,
  ColumnHeaderCell: TableColumnHeaderCell,
  Cell: TableCell,
};
